import { createLocalStorageCarrier } from '@/lib/local-storage-carrier'
import { getStorageItem, STORAGE_KEYS } from '@/lib/pwa-storage'
import { publicChatChildName } from '@/server/domain/santa-chat/public/child-name'

/**
 * El nombre del niño que pasa del chat público al alta de perfil (AC2.3): lo
 * único que se transfiere, nunca la conversación. Vive en `localStorage` para
 * sobrevivir al registro (OAuth incluido), en un `createLocalStorageCarrier`
 * (JSON + parser, sobre el guard de SSR y el try/catch de `lib/pwa-storage`).
 * Se guarda con su fecha y caduca a los 7 días: pasado ese plazo ya no se
 * ofrece y la purga lo borra (privacidad). Al leerlo se recorta y sanea como
 * lo haría el servidor: lo guardado puede venir de otra versión o estar
 * manipulado.
 */
const CHILD_NAME_TTL_MS = 7 * 24 * 60 * 60 * 1000

interface StoredChildName {
  name: string
  savedAt: number
}

function parseStored(stored: unknown): StoredChildName | null {
  if (typeof stored !== 'object' || stored === null) return null
  const { name, savedAt } = stored as Record<string, unknown>
  return typeof name === 'string' && typeof savedAt === 'number' ? { name, savedAt } : null
}

const carrier = createLocalStorageCarrier(STORAGE_KEYS.PUBLIC_CHAT_CHILD_NAME, parseStored)

/** Guarda el nombre saneado con su fecha; sin nombre útil, borra el que hubiera. */
export function storePublicChatChildName(raw: string | null, now: number = Date.now()): void {
  const name = publicChatChildName(raw)
  if (name) carrier.write({ name, savedAt: now })
  else carrier.clear()
}

/**
 * El nombre pendiente, saneado y recortado; `null` si no hay, o si está
 * caducado, ilegible o en el formato anterior (el nombre suelto, sin fecha).
 * Puro: es el `getSnapshot` del alta, así que no borra nada (eso es
 * `purgeExpiredPublicChatChildName`).
 */
export function readPublicChatChildName(now: number = Date.now()): string | null {
  const stored = carrier.read()
  return stored && isFresh(stored, now) ? publicChatChildName(stored.name) : null
}

/** Borra una entrada caducada, ilegible o del formato anterior (privacidad); una vigente se queda. */
export function purgeExpiredPublicChatChildName(now: number = Date.now()): void {
  if (getStorageItem(STORAGE_KEYS.PUBLIC_CHAT_CHILD_NAME) === null) return
  const stored = carrier.read()
  if (!stored || !isFresh(stored, now)) carrier.clear()
}

/** Tras crear el perfil el nombre ya no hace falta (privacidad). */
export function clearPublicChatChildName(): void {
  carrier.clear()
}

/** Una fecha del futuro no caducaría nunca: cuenta como manipulada. */
function isFresh({ savedAt }: StoredChildName, now: number): boolean {
  return savedAt <= now && now - savedAt <= CHILD_NAME_TTL_MS
}
